/**
 * ReceiptPreviewPane Component — T146P2 US5
 *
 * Live receipt preview of transaction form state.
 * Desktop (≥1024px): right-side sticky pane, max-h-[70vh]
 * Tablet (768–1023px): below form, full-width, max-h-[50vh]
 * Mobile (<768px): hidden
 *
 * Updates on debounce 200ms when form state changes.
 * Shows warning banner if itemsTotal > txnAmount.
 * Respects current density setting.
 *
 * Data-testids: receipt-preview-pane, receipt-preview-warning
 */

import { useMemo, useEffect, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCurrency } from '@/hooks/useCurrency';
import { useDisplaySettings } from '@/hooks/useDisplaySettings';
import ReceiptItemRow from '@/components/transactions/ReceiptItemRow';
import ItemizedSummaryBar from '@/components/transactions/ItemizedSummaryBar';
import type { TransactionItem } from '@/lib/types';
import { buildTransactionBreakdown } from '@/lib/transactionBreakdown';

interface ReceiptPreviewPaneProps {
  description?: string;
  category?: string;
  amount?: number;
  transactionDiscountAmount?: number;
  currency?: string;
  items?: TransactionItem[];
  className?: string;
  // [Rev23 G4] Edit-only acknowledgement. When true, the over-allocation
  // advisory exposes an `I understand` button that hides the warning for
  // the current Edit Preview session; the acknowledgement resets when
  // amount, transaction discount, or item totals change. Detail/read-only
  // callers omit this prop and keep the advisory persistently visible.
  dismissible?: boolean;
}

export function ReceiptPreviewPane({
  description = '(no description)',
  category,
  amount = 0,
  transactionDiscountAmount = 0,
  currency,
  items = [],
  className,
  // [Rev23 G4] When false (the Detail default), the over-allocation advisory
  // stays persistently visible and no `I understand` action is rendered.
  dismissible = false,
}: ReceiptPreviewPaneProps) {
  const { fmt } = useCurrency();
  const { itemDisplayDensity } = useDisplaySettings();
  const [debouncedItems, setDebouncedItems] = useState(items);
  const activeCurrency = currency && currency.length === 3 ? currency : 'USD';

  const formatPreviewMoney = (value: number) =>
    new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: activeCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number.isFinite(value) ? value : 0);

  // Debounce items updates by 200ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedItems(items);
    }, 200);
    return () => clearTimeout(timer);
}, [items]);

  // [R27-P] Use the shared `buildTransactionBreakdown` helper for both the live
  // (non-debounced) edit snapshot and the rendered (debounced) preview. The live
  // result drives the acknowledgement reset so the operator must re-confirm any
  // in-flight edit; the debounced result drives the visible totals, summary bar,
  // and warning so the pane stays steady during rapid typing.
  const liveBreakdown = useMemo(
    () => buildTransactionBreakdown({ amount, transactionDiscountAmount, items }),
    [amount, transactionDiscountAmount, items],
  );
  const debouncedBreakdown = useMemo(
    () => buildTransactionBreakdown({ amount, transactionDiscountAmount, items: debouncedItems }),
    [amount, transactionDiscountAmount, debouncedItems],
  );

  // [Rev23 G4] Local acknowledgement state for the Edit Preview's `I understand`
  // button. Resets to false whenever the live totals change, so the operator
  // must re-acknowledge an edit that re-introduces or worsens the
  // over-allocation. Detail callers never expose the dismiss button so this
  // state stays unused in read-only mode.
  const [warningDismissed, setWarningDismissed] = useState(false);
  // Track the live breakdown snapshot that, when changed, must reset the
  // dismiss acknowledgement. The ref guard ensures we only flip the state
  // back to false on a real transition (not on every render), so the dismiss
  // button stays useful during a single Edit session.
  const lastWatchedKeyRef = useRef<string>('');
  const watchedKey = `${liveBreakdown.transactionNet ?? 'null'}|${liveBreakdown.itemNet ?? 'null'}`;

  // [R27-P] Derive every Preview view-model value from the shared debounced
  // breakdown. The helper is the single source of truth for transaction gross,
  // discount, net, and item gross/discount/net; the pane never recomputes them
  // locally. Null breakdown values fall back to 0 so the rendered totals stay
  // numeric while incomplete data is still classified as 'incomplete' by
  // `allocationState` and therefore not surfaced as a confirmed warning.
  const transactionGross = debouncedBreakdown.transactionGross ?? 0;
  const safeTransactionDiscount = debouncedBreakdown.transactionDiscount ?? 0;
  const transactionNet = debouncedBreakdown.transactionNet ?? 0;
  const itemSubtotal = debouncedBreakdown.itemGross ?? 0;
  const itemDiscountTotal = debouncedBreakdown.itemDiscount ?? 0;
  const itemsTotal = debouncedBreakdown.itemNet ?? 0;
  const previewTotalLabel = safeTransactionDiscount > 0
    ? 'Net Total (Discounts Subtracted)'
    : 'Net Total';

  // [R27-P] Allocation state drives the warning so that incomplete data never
  // presents as a confirmed over-allocation. The helper only emits
  // 'overallocated' when amount, discount, and all items are complete AND the
  // items net exceeds the transaction net cap.
  const exceeds = debouncedBreakdown.allocationState === 'overallocated';

  // [Rev23 G4] Reset the acknowledgement whenever the live totals change.
  useEffect(() => {
    if (lastWatchedKeyRef.current !== '' && lastWatchedKeyRef.current !== watchedKey) {
      setWarningDismissed(false);
    }
    lastWatchedKeyRef.current = watchedKey;
  }, [watchedKey]);
  const showWarning = exceeds && !warningDismissed;
  const warningItemsTotal = formatPreviewMoney(itemsTotal);
  const warningTxnAmount = formatPreviewMoney(transactionNet);


  return (
    <Card
      data-testid="receipt-preview-pane"
      className={cn('flex flex-col gap-3 p-4 bg-card/50', className)}
    >
      {/* Header */}
      <div className="text-sm font-semibold text-foreground">Preview</div>

      {/* Warning Banner */}
      {showWarning && (
      <Alert
      data-testid="receipt-preview-warning"
      className="py-2 border-amber-500/60 bg-amber-50 text-amber-900 [&>svg]:text-amber-600 dark:bg-amber-950/40 dark:text-amber-100 dark:border-amber-700/60"
      >
      <AlertCircle className="h-4 w-4" />
      <AlertTitle className="text-xs font-medium">Items exceed transaction amount</AlertTitle>
      <AlertDescription className="text-xs">
      Net items total ({warningItemsTotal}) exceeds transaction net cap ({warningTxnAmount}). Saving this transaction will be rejected by the server.
      </AlertDescription>
      {dismissible && (
      <Button
      type="button"
      size="sm"
      variant="outline"
      data-testid="receipt-preview-warning-dismiss"
      aria-label="I understand the warning"
      className="mt-2 self-start min-h-[44px] min-w-[44px] border-amber-500/60 text-amber-900 hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-950/60"
      onClick={() => setWarningDismissed(true)}
      >
      I understand
      </Button>
      )}
      </Alert>
      )}
      <div className="space-y-1 border-b pb-2">
        <div className="text-xs text-muted-foreground">Transaction</div>
        <div className="font-medium text-sm truncate">{description}</div>
        {category && (
          <div className="text-xs text-muted-foreground">{category}</div>
        )}
      </div>

      {/* Items Summary Bar */}
      {debouncedItems.length > 0 && (
        <ItemizedSummaryBar
          itemizedTotal={itemsTotal}
          transactionTotal={transactionGross}
          itemSubtotal={itemSubtotal}
          itemDiscountTotal={itemDiscountTotal}
          transactionDiscountTotal={safeTransactionDiscount}
          currency={currency}
          className="mb-2"
        />
      )}

      {/* Items List */}
      <div className="space-y-1 flex-1 overflow-y-auto max-h-[calc(100vh-300px)]">
        {debouncedItems.length === 0 ? (
          <div className="text-xs text-muted-foreground/60 italic py-4">
            No items added yet
          </div>
        ) : itemDisplayDensity === 'compact' ? (
          // Compact: qty + amount only
          debouncedItems.map((item, idx) => (
            <div
              key={idx}
              className="flex justify-between items-center py-1 text-xs border-b border-border/30"
            >
              <span className="text-muted-foreground">
                {item.quantity ?? 1}x
              </span>
              <span className="font-medium">{fmt(item.amount || 0)}</span>
            </div>
          ))
        ) : itemDisplayDensity === 'detailed' ? (
          // Detailed: full row with category override indicator
          debouncedItems.map((item, idx) => (
            <div key={idx} className="border-b border-border/30">
              <ReceiptItemRow
                item={item}
                rowNumber={idx + 1}
                className="py-1 text-xs"
              />
            </div>
          ))
        ) : (
          // Receipt: standard display (default)
          debouncedItems.map((item, idx) => (
            <div key={idx} className="border-b border-border/30">
              <ReceiptItemRow
                item={item}
                rowNumber={idx + 1}
                className="py-1 text-xs"
              />
            </div>
          ))
        )}
      </div>

      {/* Total Footer */}
      <div className="border-t pt-2 flex justify-between items-center">
        <span className="text-xs font-medium" data-testid="receipt-preview-total-label">{previewTotalLabel}</span>
        <span className={cn('font-semibold', exceeds ? 'text-destructive' : '')}>
          <span data-testid="receipt-preview-total-value">{fmt(transactionNet)}</span>
        </span>
      </div>
    </Card>
  );
}
